import type { EmployeeView, ServiceView } from "../../../api/directory.api"

/**
 * ПУБЛИЧНЫЙ ПРОФИЛЬ МАСТЕРА (ТЗ 03, Б7; макет П2, 3-Б7) — правила словами.
 *
 * Чистые функции: экран раскладывает строки, а решения — опубликован ли мастер, какие
 * услуги попадут в карточку витрины, чего не хватает — проверяет тест. Те же правила
 * у сервера (booking/published-employee.ts): разойдись они — экран обещал бы «на витрине»
 * мастера, которого клиент не увидит.
 */

export interface ProfileForm {
  readonly publicName: string
  readonly positionId: string
  readonly about: string
  readonly isOnlineBookable: boolean
}

export function formOf(employee: EmployeeView): ProfileForm {
  return {
    publicName: employee.publicName ?? "",
    positionId: employee.positionId ?? "",
    about: employee.about ?? "",
    isOnlineBookable: employee.isOnlineBookable,
  }
}

/** Ошибки формы; null — можно сохранять. Имя обязательно всегда: оно и есть «как видит клиент». */
export function profileErrors(form: ProfileForm): { readonly publicName: string | null; readonly about: string | null } | null {
  const publicName = form.publicName.trim() === "" ? "Укажите имя, которое увидит клиент: «Аня», а не «Соколова А. С.»" : null
  const about = form.about.trim().length > 2000 ? "Описание длиннее 2000 знаков — клиент его не дочитает" : null
  return publicName === null && about === null ? null : { publicName, about }
}

/**
 * Где мастер сейчас: на витрине, скрыт салоном, без имени или не исполнитель. Последнее
 * важно сказать прямо: администратора записать нельзя, и выключатель ему не поможет.
 */
export type Publication =
  | { readonly kind: "published" }
  | { readonly kind: "hidden" }
  | { readonly kind: "no-name" }
  | { readonly kind: "not-bookable" }

export function publicationOf(employee: EmployeeView): Publication {
  if (!employee.isBookable) return { kind: "not-bookable" }
  if (!employee.isOnlineBookable) return { kind: "hidden" }
  if ((employee.publicName ?? "").trim() === "") return { kind: "no-name" }
  return { kind: "published" }
}

/**
 * Значок в списке сотрудников. После П2-00 мастер без публичного имени на витрину
 * не попадает — и значок «на витрине» у него был неправдой.
 */
export function publicationBadge(employee: EmployeeView): { readonly label: string; readonly tone: "ok" | "warn" } | null {
  switch (publicationOf(employee).kind) {
    case "published":
      return { label: "на витрине", tone: "ok" }
    case "hidden":
      return { label: "только через админа", tone: "warn" }
    case "no-name":
      return { label: "нет имени для витрины", tone: "warn" }
    default:
      return null
  }
}

/**
 * Услуги в карточке витрины: только те, что мастер делает И что продаются онлайн —
 * ровно так, как их покажет витрина. Две названием, остальные числом («+ 7»), как в макете.
 */
export function previewServices(
  employee: EmployeeView,
  services: readonly ServiceView[],
): { readonly names: readonly string[]; readonly more: number } {
  const shown = services.filter(
    (service) => service.isActive && service.isOnlineBookable && employee.serviceIds.includes(service.id),
  )
  return { names: shown.slice(0, 2).map((service) => service.name), more: Math.max(0, shown.length - 2) }
}

/**
 * Пустой профиль. Причина прямая и проверяемая, как требует ТЗ: «без фото записываются
 * реже», а не «добавьте фото для красоты». Без склонения имени — машина его не умеет.
 */
export function emptyProfileHint(employee: EmployeeView, form: ProfileForm): string | null {
  if (employee.avatarUrl === null) return "Клиенты выбирают мастера по фото — без него к мастеру будут записываться реже."
  if (form.about.trim() === "") return "Пара строк о мастере — опыт, специализация — помогает клиенту выбрать."
  return null
}
